import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import AlertIcon from '@/assets/icons/alert';
import ClockIcon from '@/assets/icons/clock';
import DownIcon from '@/assets/icons/down';
import { Badge } from '@/components/Badge';
import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { AccountTabNavigationProp } from '@/navigation/types';
import { common } from '@/styles/styles';
import { formatSocialCurrency } from '@/utils/numeric/currency';
import { format } from 'date-fns';

import { Transaction } from '../hooks/types';
import { getTransactionDisplayCurrency, toAmountCurrency } from '../utils/formatters';
import { CurrencyAmount } from './CurrencyAmount';
import {
    FailedTransactionReason,
    getActionTranslation,
    getSubText,
    isFailedTransaction,
    isPendingTransaction,
} from './helper';

type TransactionCardPropsType = { transaction: Transaction };

const styles = StyleSheet.create({
    rightArrow: { transform: [{ rotate: '-90deg' }] },
});

export const TransactionCard = ({ transaction }: TransactionCardPropsType) => {
    const { t } = useTranslation('wallet');
    const { amount, created_at, action, currency, status, user_wallet_id, failed_reason } = transaction;
    const { navigate } = useNavigation<AccountTabNavigationProp>();
    const symbol = amount >= 0 ? '+' : '-';
    const hasSocialWallet = useHasSocialWallet();
    const isFailed = isFailedTransaction(status);
    const isPending = isPendingTransaction(status);

    const displayCurrency = getTransactionDisplayCurrency(action, currency, hasSocialWallet);

    const displayAmount = useMemo(() => {
        const absAmount = Math.abs(amount);
        if (displayCurrency) {
            const formatted = formatSocialCurrency(absAmount, toAmountCurrency(displayCurrency));
            return isFailed ? formatted : `${symbol}${formatted}`;
        }
        const formatted = `$${absAmount}`;
        return isFailed ? formatted : `${symbol} ${formatted}`;
    }, [amount, displayCurrency, isFailed, symbol]);

    const renderTag = useCallback(() => {
        if (isFailed) {
            const label = failed_reason === FailedTransactionReason.paymentIncomplete ? t('incomplete') : t('failed');
            return (
                <Badge
                    label={label}
                    type="error"
                    textProps={{ textTransform: 'uppercase', color: 'neutral900', lineHeight: 20 }}
                    leftIcon={<AlertIcon />}
                    testID="txnFailedBadge"
                />
            );
        }
        if (isPending) {
            return (
                <Badge
                    label={t('pending')}
                    type="info"
                    textProps={{ textTransform: 'uppercase', color: 'neutral900', lineHeight: 20 }}
                    leftIcon={<ClockIcon />}
                />
            );
        }
        return null;
    }, [isFailed, isPending, failed_reason, t]);

    const subText = getSubText(transaction);

    return (
        <TouchableOpacity
            onPress={() => navigate('TransactionDetails', { userWalletId: user_wallet_id })}
            testID="transactionCard"
            accessible={false}
        >
            <SizedBox value={16} />
            <View style={[common.spaceBetweenRow]}>
                <View style={[common.flex]}>
                    <View style={[common.row, common.alignCenter]}>
                        <Text color={'neutral300'} variant="labelMedium" textTransform="uppercase">
                            {format(created_at, "MMMM d, yyyy 'at' h:mm a")}
                        </Text>
                        <SizedBox value={8} />
                        {renderTag()}
                    </View>
                    <SizedBox value={8} />
                    <Text variant="titleMedium" testID="txnType">
                        {t(getActionTranslation(action, hasSocialWallet))}
                    </Text>
                    <SizedBox value={2} />
                    {subText ? (
                        <Text variant="bodySmall" color={'neutral300'} testID="txnCardSubText">
                            {subText}
                        </Text>
                    ) : null}
                </View>
                <View style={[common.spaceBetweenRow]}>
                    <CurrencyAmount
                        currency={displayCurrency}
                        amount={displayAmount}
                        variant="bodyMedium"
                        {...(isFailed
                            ? { textDecorationLine: 'line-through', color: ds.colorToken.text.secondary }
                            : {})}
                    />
                    <SizedBox value={8} />
                    <DownIcon style={styles.rightArrow} />
                </View>
            </View>
            <SizedBox value={16} />
            <LineSeparator />
        </TouchableOpacity>
    );
};
